import "./announcer.scss";

const ASSERTIVE = "assertive";
const POLITE = "polite";
const FOCUS = "focus";
const defaultLevel = POLITE;

/**
 * Accessibility announcer
 * @class
 */
export class Announcer {
  constructor() {
    this._node = Announcer.createNode();
    this._assertiveLog = Announcer.createLog(ASSERTIVE);
    this._politeLog = Announcer.createLog(POLITE);
    this._focusLog = Announcer.createLog(FOCUS);

    this._node.appendChild(this._assertiveLog);
    this._node.appendChild(this._politeLog);
    this._node.appendChild(this._focusLog);

    // add this node just after the header, so it appears where the main
    // content of the app goes
    const headerEl = /** @type HTMLElement | null */ (
      document.querySelector("#chatbot-header")
    );
    // if we are on the chat topic screen, we need to place this in the same div
    // as the text messages
    const containerEl = /** @type HTMLElement | null */ (
      document.querySelector(".chat__topic__messages")
    );
    if (containerEl) {
      containerEl.appendChild(this._node);
      containerEl.focus();
    } else if (headerEl) {
      headerEl.parentNode?.insertBefore(this._node, headerEl.nextSibling);
    } else {
      // we must be on the attract screen or the dataviz, in which case
      // prepending to the body should work just fine.
      document.body.prepend(this._node);
    }
  }

  get node() {
    return this._node;
  }

  set node(newNode) {
    this._node = newNode;
  }

  get assertiveLog() {
    return this._assertiveLog;
  }

  set assertiveLog(newAssertiveLog) {
    this._assertiveLog = newAssertiveLog;
  }

  get politeLog() {
    return this._politeLog;
  }

  set politeLog(newPoliteLog) {
    this._politeLog = newPoliteLog;
  }

  get focusLog() {
    return this._focusLog;
  }

  set focusLog(newFocusLog) {
    this._focusLog = newFocusLog;
  }

  static createNode() {
    const n = document.createElement("div");
    n.classList.add("announcer-container");
    n.classList.add("visually-hidden");
    return n;
  }

  /**
   * Create a log container of the defined level
   * @param {POLITE | ASSERTIVE | FOCUS} level
   */
  static createLog(level) {
    const n = document.createElement("div");
    if (level !== FOCUS) {
      n.setAttribute("role", "log");
      n.setAttribute("aria-live", level);
      n.setAttribute("aria-relevant", "additions");
    }

    return n;
  }

  /**
   * Add message to an annoucer container
   * @param {Object} args - object for named args
   * @param {string} args.message
   * @param {ASSERTIVE | POLITE | FOCUS} [args.level=defaultLevel]
   * @param {number} [args.timeout=0]
   */
  announce({ message, level = defaultLevel, timeout = 0 }) {
    if (!this.node) {
      // eslint-disable-next-line no-console
      console.error("No announcer node available.");
      return;
    }

    const n = document.createElement("div");
    n.textContent = message;
    n.classList.add("text");
    n.classList.add("announcer-text");

    if (level === ASSERTIVE) {
      this.assertiveLog.appendChild(n);
    } else if (level === POLITE) {
      this.politeLog.appendChild(n);
    } else {
      n.setAttribute("tabindex", 0);
      this.focusLog.appendChild(n);
      n.focus();
      const nSilent = n.cloneNode();
      nSilent.setAttribute("aria-hidden", true);
      this.politeLog.appendChild(nSilent);
    }

    if (timeout) {
      setTimeout(() => {
        n.remove();
      }, timeout);
    }
  }

  /**
   * Clear an announcer container or series of containers
   * - To clear all containers, leave the level blank
   * - Provide a string that includes the level name to clear more than one level.
   *
   * @example
   * const a = new Announcer()
   * a.clear() // clears all
   * a.clear("polite") // clears just polite
   * a.clear("polite assertive") // clears polite and assertive
   * a.clear("polite/assertive") // also clears polite and assertive
   */
  clear(level = "") {
    if (!this.node) {
      // eslint-disable-next-line no-console
      console.error("No announcer node available.");
      return;
    }

    if (!level || level === ASSERTIVE || level.includes(ASSERTIVE)) {
      this.assertiveLog.innerHTML = "";
    }

    if (!level || level === POLITE || level.includes(POLITE)) {
      this.politeLog.innerHTML = "";
    }

    if (!level || level === FOCUS || level.includes(FOCUS)) {
      this.focusLog.innerHTML = "";
    }
  }

  /**
   * Remove the announcer container node from the page
   */
  remove() {
    if (!this.node) {
      return;
    }

    this.node.parentNode.removeChild(this.node);
    this.node = null;
  }

  /**
   * Silently add items to an announcer to shadow the ChatTopic element
   * @param {Object} args - object for named arguments
   * @param {string} args.message
   * @param {POLITE | ASSERTIVE | FOCUS} [args.level=defaultLevel]
   */
  addSilently({ message, level = defaultLevel }) {
    let n;
    if (!level || level === POLITE) {
      n = this.politeLog;
    } else if (level === ASSERTIVE) {
      n = this.assertiveLog;
    } else if (level === FOCUS) {
      n = this.focusLog;
    }

    n.removeAttribute("role");
    n.removeAttribute("aria-live");
    n.removeAttribute("aria-relevant");

    this.announce({ message, level });
  }
}
